import { Box, Button, Column, CustomSpinner, Row, ScrollArea, Text } from '~/commonComponents';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import DefaultProfileImage from '~/images/doctor.png';
import { Colors } from '~/themes/colors';
import { InterviewRow } from './InterviewRow';
import { InviteSuccessView } from './InviteSuccessView';
import { useInviteToExistingInterviewModal } from './useInviteToExistingInterviewModal';

interface IInviteToExistingInterviewModalProps {
  isOpen: boolean;
  jobPostingId: string;
  officeId: string;
  professionalId: string;
  onClose: () => void;
}

export const InviteToExistingInterviewModal = ({
  isOpen,
  jobPostingId,
  officeId,
  professionalId,
  onClose,
}: IInviteToExistingInterviewModalProps) => {
  const {
    interviews,
    isLoading,
    isLoadingInvite,
    isSuccessInvite,
    professional,
    selectedInterviewIds,
    handleInvite,
    handleToggle,
  } = useInviteToExistingInterviewModal(isOpen, jobPostingId, professionalId, officeId);

  const isDisabled = selectedInterviewIds.length === 0;

  if (!isOpen) {
    return null;
  }

  if (isSuccessInvite) {
    return (
      <InviteSuccessView
        professional={professional}
        selectedCount={selectedInterviewIds.length}
        onClose={onClose}
      />
    );
  }

  return (
    // @ts-expect-error - ConfirmPopup still written in JavaScript
    <ConfirmPopup modalStyle={{ height: 684, width: 500 }} closePopup={onClose} showClosePopupIcon>
      {isLoading ? (
        <CustomSpinner isLoading size={24} />
      ) : (
        <>
          <Column alignItems="center" justifyContent="center" gap={16} marginBottom={20}>
            <Box
              alignItems="center"
              backgroundColor={Colors.primary_100}
              borderRadius="100%"
              height={68}
              justifyContent="center"
              width={68}
            >
              {professional?.profile_image ? (
                <img
                  alt={`${professional?.full_name} profile picture`}
                  src={professional?.profile_image}
                  height="100%"
                  width="100%"
                  style={{ borderRadius: 100, objectFit: 'cover' }}
                />
              ) : (
                <img alt="" src={DefaultProfileImage} height={36} width={36} />
              )}
            </Box>
            <Text color={Colors.neutral_500}>
              Select the interviews you&apos;d like to invite{' '}
              <strong>{professional?.full_name}</strong> to.
            </Text>
          </Column>
          <ScrollArea style={{ flex: 1, width: 424 }}>
            <Column gap={8} paddingBlock={4} paddingInline={12}>
              {interviews.length === 0 ? (
                <Text color={Colors.neutral_500} textAlign="center">
                  No available interviews found.
                </Text>
              ) : (
                interviews.map((interview) => (
                  <InterviewRow
                    key={interview.onsiteInterviewId}
                    {...interview}
                    isInvited={interview.isInvited}
                    isSelected={selectedInterviewIds.includes(interview.onsiteInterviewId)}
                    onToggle={handleToggle}
                  />
                ))
              )}
            </Column>
          </ScrollArea>
          <Row alignItems="center" justifyContent="center" gap={16} marginTop={16}>
            <Button
              style={{
                alignSelf: 'center',
                width: 152,
                margin: 0,
                backgroundColor: Colors.secondary_500,
              }}
              onClick={onClose}
              text="Go Back"
            />
            <Button
              type="button"
              style={{
                alignSelf: 'center',
                width: 152,
                margin: 0,
                backgroundColor: Colors.primary_500,
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={handleInvite}
              disabled={isDisabled}
              text="Invite"
              isLoading={isLoadingInvite}
            />
          </Row>
        </>
      )}
    </ConfirmPopup>
  );
};
